import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { appointmentApi, isBookingSessionSupported, type BookingSessionPayload } from '../services/appointmentApi';
import { socketService } from '../services/socket';

/**
 * useBookingFlow
 *
 * Drives the extension-assisted appointment booking flow from a Task Detail
 * modal. Lifecycle:
 *
 *   1. preflight(task, nodeIndex)
 *        → returns { ok: true } when we can hand off to the extension, else
 *          { ok: false, reason } so the caller can fall back to the manual
 *          MakeAppointmentModal with an explanation.
 *   2. startBooking(taskId, nodeIndex)
 *        → POSTs /appointments/booking-session, forwards payload + user JWT
 *          to the Chrome extension, opens a sticky loading toast.
 *   3. Socket events from the backend drive the toast through its final state:
 *        'booking_completed'  → success toast + onCompleted(appointment)
 *        'booking_incomplete' → dismiss toast + onIncomplete(partialData)
 *
 * The hook tracks every session it started, not just the newest one. Attempts
 * overlap in practice — an operator closes the eModal tab and clicks Book
 * again, two console tabs are open on the same load — and each of those
 * sessions still has an ending owed to it. Toasts are keyed by task rather
 * than by session, so a retry replaces the previous attempt's toast instead of
 * stacking a second one that nothing can ever close.
 *
 * Callbacks are passed in via options so the owning modal controls what
 * happens on success (e.g. refresh the task) and on incomplete (e.g. open
 * MakeAppointmentModal with the partial data prefilled).
 */

const EXT_ID = import.meta.env.VITE_CHROME_EXT_ID || 'nniodjlikchoomnhdbcnlfncdcpodbib';
const EXT_PING_TIMEOUT_MS = 800;

const SUPPORTED_TERMINAL_CODES = new Set(['OICT', 'ETSOAK', 'TRAPAC_OAK']);

export type BookingPreflightReason =
  | 'no_chrome'
  | 'ext_not_installed'
  | 'ext_not_enrolled'
  | 'warehouse_not_supported'
  | 'terminal_not_supported'
  | 'no_jwt';

export interface BookingPreflightResult {
  ok: boolean;
  reason?: BookingPreflightReason;
  message?: string;
}

export interface PartialBookingData {
  facility: 'port' | 'warehouse';
  appointment_number: string | null;
  gate_code: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  status: string;
}

export interface AdditionalMatchedPayload {
  sessionId: string;
  primaryTaskId: number;
  matchedTaskId: number;
  matchedTaskBid: string;
  containerNumber: string | null;
  /** 出口回执上没有柜号，认的是订舱号。 */
  bookingNumber?: string | null;
  appointment: unknown;
}

export interface AdditionalUnmatchedPayload {
  sessionId: string;
  containerNumber: string | null;
  /** 出口回执上没有柜号，认的是订舱号（回执里的 Reference）。 */
  bookingNumber?: string | null;
  reason: 'no_task_found' | 'cross_terminal' | 'persist_failed' | 'incomplete_receipt'
    | 'missing_container' | 'export_receipt_no_pending_task_for_booking' | string;
  appointmentNumber: string | null;
}

export interface BookingUnconfirmedPayload {
  sessionId: string;
  taskId: number;
  finalStatus: string;
  /** Operator was on the time-slot screen — a real submit is plausible. */
  reachedSlotScreen: boolean;
  /**
   * The wizard had been handed over to the operator. False means we were still
   * filling it for them, so nothing could have been submitted — close the
   * spinner quietly instead of sending them to go check eModal.
   */
  handedOver?: boolean;
}

/**
 * One toast per task leg, not one per session.
 *
 * The toast is the operator's answer to "did my click do anything", and that
 * question belongs to the task, not to whichever attempt happens to be running.
 * Keying it per session is what let a superseded attempt leave a spinner behind
 * that nothing owned and nothing could close. With a task-scoped id a retry
 * simply overwrites the first attempt's toast, and there is never a second one
 * to orphan.
 *
 * Exported because the Book button opens this toast on click — before preflight
 * and before any session exists — so the operator gets feedback in the same
 * frame they clicked, not 800ms later.
 */
export function bookingToastId(taskId: number | string): string {
  return `booking-task-${taskId}`;
}

/**
 * Last-resort watchdog for the sticky "Opening eModal" toast.
 *
 * Normally one of booking_completed / booking_incomplete / booking_unconfirmed
 * resolves it. All three can go missing at once — the extension's service
 * worker is killed while the operator lingers on the slot screen, so nothing
 * ever reports the outcome. Slightly longer than the content script's own
 * 10-minute receipt watchdog so a late-but-real receipt always wins the race.
 */
const BOOKING_WATCHDOG_MS = 10.5 * 60 * 1000;

/**
 * 出口还重柜那一程会在开向导**之前**插一步：先在 eModal 上把闸口费申领下来，
 * 而最后那下 SUBMIT 是**人**按的（插件那边按每条订舱号等 5 分钟）。
 *
 * 看门狗原本那 10.5 分钟是只留给向导的。不把这一步算进去的话，调度员还在
 * eModal 上按那一下，这边就先弹一句「没确认」—— 而那一单其实好好的。
 */
const CLAIM_EXTRA_MS = 6 * 60 * 1000;
const watchdogFor = (payload: BookingSessionPayload) => {
  const n = payload.export_gate_fee_claim?.bookings?.length || 0;
  return BOOKING_WATCHDOG_MS + n * CLAIM_EXTRA_MS;
};

/**
 * 「码头出票了、票还在路上」—— 收尾当场唯一说得准的一句话。
 *
 * 只说别重约。叫人去 eModal 手抄要等复核完：那张票多半一两分钟后就自己进来了，
 * 提前喊出去的代价是重复记录，最坏是有人真去重约 = 同柜两张票。
 */
/**
 * 屏幕上怎么称呼一只柜。
 *
 * 进口叫柜号。出口在提柜之前**没有柜号**（空柜是进闸时码头现场指的），能认出
 * 「这是哪一票」的只有订舱号。少了这个兜底，出口批量的漏单提示会念成
 * 「null, null 这两只柜没约上」—— 而这条提示恰恰是漏单唯一被人看见的地方。
 */
const boxLabel = (m: {
  containerNumber?: string | null;
  bookingNumber?: string | null;
  taskNumber?: string | null;
  taskId?: number;
}) => m.containerNumber || m.bookingNumber || m.taskNumber || `#${m.taskId ?? '?'}`;

const ghostAwaitingMessage = (containers: string[]) =>
  `${containers.join(', ')}: eModal made the appointment — the receipt hasn't reached us yet, `
  + 'and we\'re pulling it in automatically. Do NOT book these again.';

/** 复核完仍然没拿到票。现在才该请人动手，并且报得出票号。 */
const ghostMissingMessage = (
  moves: Array<{
    containerNumber?: string | null;
    bookingNumber?: string | null;
    taskNumber?: string | null;
    taskId?: number;
    terminalTicketNumber?: string | null;
  }>
) => {
  const tickets = moves.map((m) => m.terminalTicketNumber).filter(Boolean);
  return `${moves.map(boxLabel).join(', ')}: eModal made the appointment but we did not `
    + 'get the receipt. Check My Appointments on eModal'
    + `${tickets.length ? ` (TcktNr ${tickets.join(', ')})` : ''} and record it by hand — `
    + 'do NOT book these again.';
};

const UNCONFIRMED_MESSAGE =
  'eModal booking not confirmed — if you completed it there, we did not get it back. ' +
  'Check My Appointments on eModal before booking again, then record it here.';

/**
 * Extension refusal codes, in words an operator can act on. Left as-is when
 * unmapped — an unfamiliar code on screen beats a wrong reassuring sentence.
 */
const HANDOFF_ERRORS: Record<string, string> = {
  // 「还开着」只说对了一半：出口还重柜那一程会先去申领闸口费，那十几秒里
  // 标签页还没开出来，但这一单确实已经在走了 —— 两种情形同一句话要都成立。
  ALREADY_BOOKING:
    'Another eModal booking is already underway in this browser — wait for it, or close that tab first.',
  USER_JWT_MISSING: 'Session expired — please sign in again.',
  TAB_OPEN_FAILED: 'Could not open the eModal tab — please enter the appointment manually.',
};

/** One in-flight booking session started by this tab. */
interface BookingSessionRecord {
  taskId: number;
  /** Task-scoped, so a retry reuses the same toast rather than adding one. */
  toastId: string;
  /** This session's own last-resort timer — never shared with another session. */
  timer: ReturnType<typeof setTimeout> | null;
  /** 批量预约时这一次装了几只柜（1 = 普通单柜）。只用来措辞。 */
  batchSize: number;
}

/**
 * 批量预约收尾摘要：这一批 N 只柜，哪些拿到了票、哪些没有。
 *
 * 只在**有柜子没约上**时才会发出来。eModal 对超订是静默失败的（HTTP 200、
 * 所有严重程度字段都是 0），没有这条事件，调度会以为整批都成了。
 */
export interface BatchBookingSummaryPayload {
  sessionId: string;
  total: number;
  bookedCount: number;
  failedCount: number;
  moves: Array<{
    taskId: number;
    taskNumber: string | null;
    /** 出口柜在提柜前没有柜号 —— 那种情况下这里是 null，名字靠 bookingNumber。 */
    containerNumber: string | null;
    bookingNumber?: string | null;
    outcome: 'booked' | 'rejected' | 'unmatched' | 'abandoned' | 'pending';
    reason: string | null;
    appointmentId: number | null;
    /**
     * 码头说这只柜建成了，但票没到我们这里 —— 它在 eModal 上**有真票**。
     * 这类柜绝不能提示「重约」，否则码头上会多出第二张票。
     */
    terminalHasTicket: boolean;
    /** 码头提交时给这只柜的票号 —— 真要人去 eModal 上找，得报得出号。 */
    terminalTicketNumber?: string | null;
  }>;
  /** 上面那种柜有几只。>0 时整条提示要换一套说法。 */
  terminalHasTicketCount: number;
  /**
   * 后端这会儿还在自动补取那些票（回执常常只回主柜一张，其余的由 eModal
   * 同步在一两分钟后补进来）。true = 提示只能说「别重约」，还不能叫人去手抄。
   */
  awaitingTicket?: boolean;
}

/**
 * 「码头有票、我们没收到」复核完的结果。
 *
 * 补回来了 → recovered；到点还是没有 → phase='missing'，这才轮到那句
 * 「去 My Appointments 手抄、别再约」。
 */
export interface BatchTicketStatusPayload {
  sessionId: string;
  phase: 'recovered' | 'missing';
  recovered: Array<{
    taskId: number;
    containerNumber: string | null;
    bookingNumber?: string | null;
    appointmentId: number;
    appointmentNumber: string | null;
  }>;
  missing: Array<{
    taskId: number;
    taskNumber: string | null;
    containerNumber: string | null;
    bookingNumber?: string | null;
    terminalTicketNumber: string | null;
  }>;
}

export interface UseBookingFlowOptions {
  /** Fired when backend confirms the receipt landed cleanly. */
  onCompleted?: (payload: { sessionId: string; taskId: number; appointment: unknown }) => void;
  /** Fired when receipt was missing required fields; caller should open the manual modal prefilled. */
  onIncomplete?: (payload: { sessionId: string; taskId: number; partialData: PartialBookingData; reason: string }) => void;
  /** Fired when the session ended with no receipt at all — eModal may hold an appointment we don't have. */
  onUnconfirmed?: (payload: BookingUnconfirmedPayload) => void;
  /** B1 — fired for each additional receipt matched to a task in this tenant. */
  onAdditionalMatched?: (payload: AdditionalMatchedPayload) => void;
  /** B1 — fired for each additional receipt that couldn't be matched. */
  onAdditionalUnmatched?: (payload: AdditionalUnmatchedPayload) => void;
  /** 批量预约收尾，且**有柜子没约上**时触发。 */
  onBatchSummary?: (payload: BatchBookingSummaryPayload) => void;
}

export interface BookingContext {
  /** Port (node 0 = pick leg, 1 = return leg) or warehouse. */
  facility: 'port' | 'warehouse';
  /** For port: which route node the Book button belongs to. */
  nodeIndex?: number;
  /** Supported terminal code if known up-front (e.g. pulled from task.port metadata). */
  terminalCode?: string | null;
}

function pingExtension(timeoutMs: number): Promise<{ installed: boolean; enrolled: boolean }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ installed: false, enrolled: false });
      return;
    }
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve({ installed: false, enrolled: false });
      }
    }, timeoutMs);
    try {
      chrome.runtime.sendMessage(EXT_ID, { type: 'ping' }, (resp: { ok?: boolean; state?: string } | undefined) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (chrome.runtime.lastError || !resp?.ok) {
          resolve({ installed: false, enrolled: false });
          return;
        }
        resolve({ installed: true, enrolled: resp.state === 'authenticated' });
      });
    } catch {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        resolve({ installed: false, enrolled: false });
      }
    }
  });
}

function sendEmodalBookingStart(payload: {
  sessionId: string;
  payload: BookingSessionPayload;
  userJwt: string;
}): Promise<{ ok: boolean; error?: string }> {
  return new Promise((resolve) => {
    if (!window.chrome?.runtime?.sendMessage) {
      resolve({ ok: false, error: 'Chrome runtime unavailable' });
      return;
    }
    try {
      chrome.runtime.sendMessage(
        EXT_ID,
        { type: 'EMODAL_BOOKING_START', ...payload },
        (resp: { ok?: boolean; error?: string } | undefined) => {
          if (chrome.runtime.lastError) {
            resolve({ ok: false, error: chrome.runtime.lastError.message });
          } else {
            resolve({ ok: !!resp?.ok, error: resp?.error });
          }
        }
      );
    } catch (e) {
      resolve({ ok: false, error: (e as Error).message });
    }
  });
}

export function useBookingFlow(options: UseBookingFlowOptions = {}) {
  const {
    onCompleted, onIncomplete, onUnconfirmed,
    onAdditionalMatched, onAdditionalUnmatched, onBatchSummary,
  } = options;

  /**
   * Every session this tab started, until it reaches an ending.
   *
   * This was a single "active session" ref, which quietly encoded an
   * assumption nothing enforced: that only one booking is ever in flight.
   * The moment a second one started — a retry after the first eModal tab was
   * closed, two console tabs, a mis-click — the first session's id was
   * overwritten and every one of its events was then dropped by the
   * `!== activeSessionRef` guards. Its toast had no owner left and spun until
   * the page was reloaded. A map keeps each session addressable so all of them
   * reach an ending, in any order.
   */
  const sessionsRef = useRef<Map<string, BookingSessionRecord>>(new Map());
  // For B1 dual-booking: additional_matched/unmatched events arrive AFTER
  // booking_completed has already nulled activeSessionRef. We keep the
  // session id alive in a separate ref for ~60s post-completion so we
  // still toast the dual-match outcomes for the same operator.
  const recentSessionRef = useRef<string | null>(null);
  const recentSessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * 「码头有票、我们还没收到」的那些柜，按会话记着。
   *
   * 复核的结果最晚 5 分钟后才到，早过了上面那个 60 秒的窗口 —— 少了这张表，
   * 归属守卫会把它丢掉，屏幕上那条「正在自动补取」就永远不会有下文。
   * 只有**开这次预约的那个 Hook 实例**会记上，所以同一条提示不会弹两份。
   */
  const ghostSessionsRef = useRef<Map<string, string>>(new Map());
  // Keep callbacks current without resubscribing sockets.
  const onCompletedRef = useRef(onCompleted);
  const onIncompleteRef = useRef(onIncomplete);
  const onUnconfirmedRef = useRef(onUnconfirmed);
  const onAdditionalMatchedRef = useRef(onAdditionalMatched);
  const onAdditionalUnmatchedRef = useRef(onAdditionalUnmatched);
  const onBatchSummaryRef = useRef(onBatchSummary);
  /**
   * Retire a session: cancel its own watchdog and forget it.
   *
   * Returns the record so callers can address the right toast. Deliberately
   * per-session — the shared watchdog ref this replaces meant starting a second
   * booking cancelled the FIRST one's last-resort timer, removing the very
   * safety net that existed for the case where nothing reports back.
   */
  const retireSession = (sessionId: string): BookingSessionRecord | null => {
    const record = sessionsRef.current.get(sessionId);
    if (!record) return null;
    if (record.timer) clearTimeout(record.timer);
    sessionsRef.current.delete(sessionId);
    return record;
  };

  /** Is another session still working on this task's toast? */
  const taskHasLiveSession = (taskId: number): boolean => {
    for (const record of sessionsRef.current.values()) {
      if (record.taskId === taskId) return true;
    }
    return false;
  };

  useEffect(() => { onCompletedRef.current = onCompleted; }, [onCompleted]);
  useEffect(() => { onIncompleteRef.current = onIncomplete; }, [onIncomplete]);
  useEffect(() => { onUnconfirmedRef.current = onUnconfirmed; }, [onUnconfirmed]);
  useEffect(() => { onAdditionalMatchedRef.current = onAdditionalMatched; }, [onAdditionalMatched]);
  useEffect(() => { onAdditionalUnmatchedRef.current = onAdditionalUnmatched; }, [onAdditionalUnmatched]);
  useEffect(() => { onBatchSummaryRef.current = onBatchSummary; }, [onBatchSummary]);

  useEffect(() => {
    // The Map instance itself never changes — captured here only so the
    // cleanup below isn't reading a ref at teardown time.
    const sessions = sessionsRef.current;

    const startRecentSessionWindow = (sessionId: string) => {
      recentSessionRef.current = sessionId;
      if (recentSessionTimerRef.current) clearTimeout(recentSessionTimerRef.current);
      // Backend matches additional receipts synchronously inside the
      // /complete handler, so events arrive within a single round-trip
      // of booking_completed. 60s is generous slack for slow networks.
      recentSessionTimerRef.current = setTimeout(() => {
        if (recentSessionRef.current === sessionId) recentSessionRef.current = null;
        recentSessionTimerRef.current = null;
      }, 60_000);
    };

    const handleCompleted = (data: { sessionId: string; taskId: number; appointment: { appointment_number?: string | null } }) => {
      const record = retireSession(data.sessionId);
      if (!record) return;
      const apptNo = data.appointment?.appointment_number;
      // Success always wins the task's toast, even if a newer attempt is still
      // spinning on it: the appointment exists, and that is the thing the
      // operator needs to see.
      toast.success(
        apptNo ? `Appointment booked — TcktNr ${apptNo}` : 'Appointment booked',
        { id: record.toastId }
      );
      // Hand off to the recent-session window so dual-match events still
      // toast for the same operator's session.
      startRecentSessionWindow(data.sessionId);
      onCompletedRef.current?.(data);
    };
    const handleIncomplete = (data: { sessionId: string; taskId: number; partialData: PartialBookingData; reason: string }) => {
      const record = retireSession(data.sessionId);
      if (!record) return;
      // Only clear the spinner if no later attempt is now using it.
      if (!taskHasLiveSession(record.taskId)) toast.dismiss(record.toastId);
      // 'receipt_container_mismatch' 不是「读不全」，是「这张回执讲的是另一只柜」——
      // 后端宁可一个字都不写也不肯挑一张任务塞进去。照抄「请在弹窗里补完」会让人
      // 对着一个空框发呆，得说清楚该去哪儿看。
      toast.warning(
        data.reason === 'receipt_container_mismatch'
          ? 'That eModal receipt is for a different container — check My Appointments and record it by hand'
          : 'Receipt incomplete — please finish in the modal',
        { duration: 4000 }
      );
      // 和 handleCompleted 一样打开"刚结束的会话"窗口：批量收尾摘要
      // (batch_booking_summary) 跟在这条后面到，而会话已经被摘掉了。
      // 少了这一句，主回执读不出来的那一批，漏单提示会被自己的归属守卫丢掉。
      startRecentSessionWindow(data.sessionId);
      onIncompleteRef.current?.(data);
    };
    /**
     * The session ended with no receipt.
     *
     * Two very different endings share this event. Past handover an
     * appointment may exist at eModal that we have no record of — that one is
     * never auto-dismissed, because a toast that fades after four seconds is
     * how that fact gets lost. Before handover we were still filling the form,
     * so nothing could have been submitted: close the spinner and say so
     * plainly, without sending anyone to go check eModal for nothing.
     */
    const handleUnconfirmed = (data: BookingUnconfirmedPayload) => {
      const record = retireSession(data.sessionId);
      if (!record) return;
      const superseded = taskHasLiveSession(record.taskId);
      if (!superseded) toast.dismiss(record.toastId);
      // 同上：整批被码头拒掉时走的就是这条路，收尾摘要紧随其后。
      startRecentSessionWindow(data.sessionId);
      if (data.reachedSlotScreen) {
        toast.error(UNCONFIRMED_MESSAGE, { duration: Infinity, closeButton: true });
      } else if (data.handedOver) {
        toast.warning('eModal booking was not finished — nothing was recorded. Book again when ready.', { duration: 6000 });
      }
      // 第三种结局（没交到人手上就没了）只收圈，不出声：人自己按的取消，
      // 而且什么都没发生过 —— 告诉他「你取消了」是把他刚做的动作念回去。
      onUnconfirmedRef.current?.(data);
    };
    const handleAdditionalMatched = (data: AdditionalMatchedPayload) => {
      // Accept if either still active or in the post-completion window.
      if (!sessionsRef.current.has(data.sessionId) && data.sessionId !== recentSessionRef.current) return;
      const containerLabel = data.containerNumber || data.bookingNumber || 'unknown container';
      const taskLabel = data.matchedTaskBid || `task ${data.matchedTaskId}`;
      toast.info(`Auto-matched booking for ${taskLabel} (${containerLabel})`, { duration: 6000 });
      onAdditionalMatchedRef.current?.(data);
    };
    const handleAdditionalUnmatched = (data: AdditionalUnmatchedPayload) => {
      if (!sessionsRef.current.has(data.sessionId) && data.sessionId !== recentSessionRef.current) return;
      const containerLabel = data.containerNumber || data.bookingNumber || 'unknown container';
      const reasonLabel: Record<string, string> = {
        no_task_found: 'no matching task in this tenant',
        cross_terminal: 'task is on a different terminal',
        persist_failed: 'failed to save the appointment',
        incomplete_receipt: 'receipt missing required fields',
        missing_container: 'receipt has no container number',
        // 出口：这张票的订舱号我们认得，但这一批里那张订舱号下已经没有
        // 还等着票的任务了 —— 多回来一张票（码头给的比要的多）。
        export_receipt_no_pending_task_for_booking:
          'this booking has no container left waiting for a slot in this batch',
      };
      const detail = reasonLabel[data.reason] || data.reason;
      toast.warning(`Could not auto-match ${containerLabel} — ${detail}`, { duration: 8000 });
      onAdditionalUnmatchedRef.current?.(data);
    };
    /**
     * 批量收尾，有柜子没约上。
     *
     * 这条 toast **不自动消失**。码头砍掉一只柜是完全无声的（HTTP 200、所有
     * severity 字段都是 0），调度这会儿正盯着"预约成功"的绿字；一条四秒后
     * 淡出的提示，等于这件事没发生过 —— 直到司机开到码头。
     *
     * 不受 sessionsRef 存活与否影响：它跟在 booking_completed 后面到，
     * 而那条已经把会话摘掉了。有没有它，这一批的漏单都必须说出来。
     */
    const handleBatchSummary = (data: BatchBookingSummaryPayload) => {
      if (!data || data.failedCount <= 0) return;
      // 只认自己开的那些会话。这个 Hook 在页面上有不止一个实例（列表一个、
      // 任务详情弹窗一个），不设防的话同一条漏单会弹两条一模一样的红字。
      //
      // 用 recentSession 而不是 sessionsRef：这条事件跟在 booking_completed
      // 后面到，而那条已经把会话摘掉了。
      if (!sessionsRef.current.has(data.sessionId) && data.sessionId !== recentSessionRef.current) return;
      const failed = data.moves.filter((m) => m.outcome !== 'booked');

      // 两类失败，给的建议正好相反，所以分开说。
      //
      //   没出票   → 去重约
      //   出了票但我们没收到 → **千万别重约**，去 eModal 上把它抄回来
      //
      // 混成一句「这些还需要时段」的代价是：调度照做，同一只柜在码头上出现
      // 第二张票。这是这个系统能给出的最坏的一句建议。
      const ghosts = failed.filter((m) => m.terminalHasTicket);
      const needSlot = failed.filter((m) => !m.terminalHasTicket);

      if (needSlot.length) {
        const why = needSlot.find((m) => m.reason)?.reason;
        toast.error(
          `${data.bookedCount} of ${data.total} containers were booked. `
          + `eModal did NOT create an appointment for ${needSlot.map(boxLabel).join(', ')}`
          + `${why ? ` — ${why}` : ''}. These still need a slot.`,
          { duration: Infinity, closeButton: true }
        );
      }
      if (ghosts.length) {
        // 这一刻还不知道这张票是不是真丢了。回执一次只保证回主柜那一张，其余的柜
        // 常常由 eModal 同步在一两分钟后补进来 —— 2026-09-28 线上那一批等了 78 秒，
        // 而屏幕上已经挂着「去手抄、别再约」。叫人去做系统马上要自己做的事，
        // 轻则重复记录，重则有人干脆去重约 = 同柜两张票。
        //
        // 所以先只说那句无论如何都成立的话：**别重约**。后端复核完再用
        // batch_booking_ticket_status 给这条提示写下文（补回来了就收掉，
        // 真没补回来才升级成「去手抄」）。
        const toastId = `batch-ghost-${data.sessionId}`;
        ghostSessionsRef.current.set(data.sessionId, toastId);
        const containers = ghosts.map(boxLabel);
        if (data.awaitingTicket === false) {
          toast.error(ghostMissingMessage(ghosts), { id: toastId, duration: Infinity, closeButton: true });
        } else {
          toast.warning(ghostAwaitingMessage(containers), { id: toastId, duration: Infinity, closeButton: true });
        }
      }
      onBatchSummaryRef.current?.(data);
    };
    /**
     * 复核回来了：那几张「码头有票、我们没收到」的票到底补回来没有。
     *
     * 只有开这次预约的那个实例记着 toastId，所以这里天然不会弹重。
     */
    const handleTicketStatus = (data: BatchTicketStatusPayload) => {
      const toastId = ghostSessionsRef.current.get(data.sessionId);
      if (!toastId) return;
      const missing = data.missing || [];
      if (missing.length === 0) {
        // 票全补回来了。板上那几行已经画着预约了 —— 成功不出声，收掉提示就行。
        ghostSessionsRef.current.delete(data.sessionId);
        toast.dismiss(toastId);
        return;
      }
      if (data.phase === 'missing') {
        // 补了五分钟还是没有。现在才轮到那句「去 My Appointments 手抄」。
        ghostSessionsRef.current.delete(data.sessionId);
        toast.error(ghostMissingMessage(missing), { id: toastId, duration: Infinity, closeButton: true });
        return;
      }
      // 补回来一部分，剩下的还在等。话不变，名单缩短。
      toast.warning(
        ghostAwaitingMessage(missing.map(boxLabel)),
        { id: toastId, duration: Infinity, closeButton: true }
      );
    };
    // socketService.on is typed (...args: unknown[]), so every payload-shaped
    // handler needs the same widening. One helper instead of a cast per line.
    const asHandler = (fn: (data: never) => void) => fn as unknown as (...args: unknown[]) => void;
    const subscriptions: Array<[string, (...args: unknown[]) => void]> = [
      ['booking_completed', asHandler(handleCompleted as (data: never) => void)],
      ['booking_incomplete', asHandler(handleIncomplete as (data: never) => void)],
      ['booking_unconfirmed', asHandler(handleUnconfirmed as (data: never) => void)],
      ['booking_additional_matched', asHandler(handleAdditionalMatched as (data: never) => void)],
      ['booking_additional_unmatched', asHandler(handleAdditionalUnmatched as (data: never) => void)],
      ['batch_booking_summary', asHandler(handleBatchSummary as (data: never) => void)],
      ['batch_booking_ticket_status', asHandler(handleTicketStatus as (data: never) => void)],
    ];
    subscriptions.forEach(([event, handler]) => socketService.on(event, handler));
    return () => {
      subscriptions.forEach(([event, handler]) => socketService.off(event, handler));
      if (recentSessionTimerRef.current) clearTimeout(recentSessionTimerRef.current);
      for (const record of sessions.values()) {
        if (record.timer) clearTimeout(record.timer);
      }
      sessions.clear();
    };
  }, []);

  /**
   * Preflight. Runs cheap checks locally — extension ping + facility check —
   * before the caller commits to an HTTP round trip. Backend will still
   * validate terminal support and task completeness when createBookingSession
   * is called (returns NOT_SUPPORTED / blockers codes on failure).
   */
  const preflight = useCallback(async (ctx: BookingContext): Promise<BookingPreflightResult> => {
    if (ctx.facility === 'warehouse') {
      return { ok: false, reason: 'warehouse_not_supported', message: 'eModal does not book warehouse appointments' };
    }
    if (ctx.terminalCode && !SUPPORTED_TERMINAL_CODES.has(ctx.terminalCode)) {
      return { ok: false, reason: 'terminal_not_supported', message: `Terminal ${ctx.terminalCode} is not in the supported list` };
    }
    if (!localStorage.getItem('token')) {
      return { ok: false, reason: 'no_jwt', message: 'Not signed in' };
    }
    if (!window.chrome?.runtime?.sendMessage) {
      return { ok: false, reason: 'no_chrome', message: 'Chrome extension runtime not available' };
    }
    const { installed, enrolled } = await pingExtension(EXT_PING_TIMEOUT_MS);
    if (!installed) return { ok: false, reason: 'ext_not_installed', message: 'EAloom extension not detected' };
    if (!enrolled)  return { ok: false, reason: 'ext_not_enrolled',  message: 'EAloom extension not enrolled — please sign in again' };
    return { ok: true };
  }, []);

  /**
   * Kick off the full flow. The caller already passed preflight.
   * Returns the sessionId once the extension has accepted the message.
   * Sticky toast is managed here; the final state lands via socket events.
   */
  const startBooking = useCallback(async (
    taskId: number,
    nodeIndex = 0
  ): Promise<{ ok: boolean; sessionId?: string; error?: string; reused?: boolean }> => {
    return beginSession([taskId], nodeIndex);
  }, []);

  /**
   * 批量：一次 eModal 向导里约多只柜。
   *
   * 传一只柜进来和 startBooking 完全等价 —— 后端只在收到两只及以上时才走批量
   * 那条路。所以弹窗不需要自己判断"够不够格算批量"，勾几个就传几个。
   */
  const startBatchBooking = useCallback(async (
    taskIds: number[]
  ): Promise<{ ok: boolean; sessionId?: string; error?: string; reused?: boolean }> => {
    if (!taskIds.length) return { ok: false, error: 'No containers selected' };
    // 批量是「N 张任务各自的提柜程」，没有第二程的概念，恒为 0。
    return beginSession(taskIds, 0);
  }, []);

  async function beginSession(
    taskIds: number[],
    nodeIndex = 0
  ): Promise<{ ok: boolean; sessionId?: string; error?: string; reused?: boolean }> {
    const userJwt = localStorage.getItem('token');
    if (!userJwt) return { ok: false, error: 'Not signed in' };

    const primaryTaskId = taskIds[0];
    const batchSize = taskIds.length;

    // The backend returns either a full booking payload (eligible task) or a
    // { notSupported: true, ... } body (HTTP 200) for routine ineligibility
    // like rail ramps or missing BOL. The non-support case is a clean
    // fall-through to the manual modal, not an error.
    let result;
    try {
      result = batchSize > 1
        ? await appointmentApi.createBatchBookingSession(taskIds)
        : await appointmentApi.createBookingSession(primaryTaskId, nodeIndex);
    } catch (err) {
      return { ok: false, error: (err as Error).message || 'Failed to create booking session' };
    }
    if (!isBookingSessionSupported(result)) {
      return { ok: false, error: result.message };
    }

    const payload: BookingSessionPayload = result;
    const { sessionId } = payload;
    const toastId = bookingToastId(primaryTaskId);

    // The backend hands the same session back when one is already live for
    // this task, so a duplicate click lands here with an id we already hold.
    // Re-registering it would leak the previous record's timer.
    retireSession(sessionId);
    sessionsRef.current.set(sessionId, { taskId: primaryTaskId, toastId, timer: null, batchSize });
    toast.loading(
      result.reused
        ? 'Returning you to the eModal booking already open for this task'
        : batchSize > 1
          ? `Opening eModal with ${batchSize} containers — please pick a time for each and submit`
          : 'Opening eModal — please complete the booking in the new tab',
      { id: toastId }
    );

    const handoff = await sendEmodalBookingStart({ sessionId, payload, userJwt });
    if (!handoff.ok) {
      retireSession(sessionId);
      if (!taskHasLiveSession(primaryTaskId)) toast.dismiss(toastId);
      return { ok: false, sessionId, error: HANDOFF_ERRORS[handoff.error || ''] || handoff.error || 'Extension failed to start booking' };
    }

    // Nothing downstream is guaranteed to report back — that is the whole
    // problem this arms against. Left alone the loading toast spins forever
    // and the operator reads it as "still working", which is exactly when a
    // real eModal appointment goes unrecorded.
    //
    // Each session arms its OWN timer. When this was one shared ref, starting
    // a second booking disarmed the first one's — so the session most likely
    // to need a safety net was the one guaranteed not to have it.
    const record = sessionsRef.current.get(sessionId);
    if (record) {
      record.timer = setTimeout(() => {
        const live = sessionsRef.current.get(sessionId);
        if (!live) return;
        sessionsRef.current.delete(sessionId);
        if (!taskHasLiveSession(primaryTaskId)) toast.dismiss(toastId);
        toast.error(UNCONFIRMED_MESSAGE, { duration: Infinity, closeButton: true });
      }, watchdogFor(payload));
    }

    return { ok: true, sessionId, reused: result.reused };
  }

  return { preflight, startBooking, startBatchBooking };
}
